import { useRef } from 'react';

import AddPhotoAlternateRoundedIcon from '@mui/icons-material/AddPhotoAlternateRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { Box, CircularProgress, IconButton, Stack, Typography } from '@mui/material';

import { useUploadAsset } from '../../hooks';
import { MEDIA_OWNER_TYPE } from '../../model/options';
import type { ToastSeverity } from '../../lib/useToast';

interface MediaManagerProps {
  /** The owning product id (omitted for a not-yet-saved product). */
  ownerId?: string;
  /** Current image URLs (the product's gallery). */
  images: string[];
  onChange: (images: string[]) => void;
  disabled?: boolean;
  onResult: (severity: ToastSeverity, message: string) => void;
}

const TILE = 96;

/**
 * Product photo gallery — uploads to web-media-service (via the BFF) and appends the returned
 * stable public URL to the product's images. Removing a tile detaches the URL from the product
 * (the asset itself is managed in the media service).
 */
export function MediaManager({ ownerId, images, onChange, disabled, onResult }: MediaManagerProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const upload = useUploadAsset();

  const handleFile = (file: File | undefined) => {
    if (!file) return;
    upload.mutate(
      { file, ownerType: MEDIA_OWNER_TYPE, ownerId },
      {
        onSuccess: (asset) => {
          onChange([...images, asset.publicUrl]);
          onResult('success', 'Фото загружено.');
        },
        onError: (error) => onResult('error', error.message),
      },
    );
  };

  const removeAt = (index: number) => onChange(images.filter((_, i) => i !== index));

  return (
    <Stack spacing={1.5}>
      <Stack direction="row" spacing={1.5} useFlexGap flexWrap="wrap">
        {images.map((url, index) => (
          <Box
            key={`${url}-${index}`}
            sx={{
              position: 'relative',
              width: TILE,
              height: TILE,
              borderRadius: 2,
              overflow: 'hidden',
              border: (t) => `1px solid ${t.palette.divider}`,
            }}
          >
            <Box
              component="img"
              src={url}
              alt={`Фото ${index + 1}`}
              sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
            {!disabled ? (
              <IconButton
                size="small"
                aria-label={`Удалить фото ${index + 1}`}
                onClick={() => removeAt(index)}
                sx={{
                  position: 'absolute',
                  top: 2,
                  right: 2,
                  bgcolor: 'rgba(0,0,0,0.55)',
                  color: '#fff',
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.75)' },
                }}
              >
                <CloseRoundedIcon sx={{ fontSize: 16 }} />
              </IconButton>
            ) : null}
          </Box>
        ))}

        {!disabled ? (
          <Box
            component="button"
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={upload.isPending}
            sx={{
              width: TILE,
              height: TILE,
              borderRadius: 2,
              border: (t) => `1px dashed ${t.palette.divider}`,
              bgcolor: 'action.hover',
              cursor: upload.isPending ? 'default' : 'pointer',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.5,
              color: 'text.secondary',
            }}
          >
            {upload.isPending ? (
              <CircularProgress size={20} />
            ) : (
              <>
                <AddPhotoAlternateRoundedIcon fontSize="small" />
                <Typography variant="caption">Загрузить</Typography>
              </>
            )}
          </Box>
        ) : null}
      </Stack>

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        hidden
        onChange={(e) => {
          handleFile(e.target.files?.[0]);
          e.target.value = '';
        }}
      />
      {images.length === 0 && disabled ? (
        <Typography variant="caption" color="text.secondary">
          Нет фотографий.
        </Typography>
      ) : null}
    </Stack>
  );
}
